import { useEffect, useState } from "react";
import { ChipStatus } from "../components/shared/ppr-chip-status/ppr-chip-status";
import {
  capitalizeFirstChar,
  extractMajorVer,
} from "../components/util/stringUtils";
import { sleep } from "../components/util/util";

export type BrowserInfo = {
  name: string;
  version: string;
};

const SUPPORTED_BROWSERS = ["edge", "chrome"];
const MIN_SUPPORTED_VERSIONS: Record<string, number> = {
  edge: parseInt(process.env.REACT_APP_EDGE_MIN_VERSION ?? "96", 10),
  chrome: parseInt(process.env.REACT_APP_CHROME_MIN_VERSION ?? "96", 10),
};

export const useBrowserCheck = (shouldRun: boolean) => {
  const [browserInfo, setBrowserInfo] = useState<BrowserInfo>();
  const [status, setStatus] = useState<ChipStatus>("not-started");

  const getBrowserInfo = async (): Promise<BrowserInfo> => {
    setStatus("analyzing");
    await sleep();
    // Detect Brave Browser only
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    if (navigator.brave && (await navigator.brave.isBrave())) {
      return { name: "brave", version: "Unknown Version" };
    }

    // Detect common browsers
    const browserInfoList = parseBrowserList();
    if (!browserInfoList || (browserInfoList && browserInfoList.length === 0)) {
      return {
        name: "Unknown",
        version: "Unknown Version",
      };
    }
    const res = browserInfoList.find((browserInfo) => {
      if (browserInfo.name === "opr") {
        browserInfo.name = "opera";
        return true;
      }

      if (
        browserInfo.name === "edg" ||
        browserInfo.name === "edge" ||
        browserInfo.name === "edgios" ||
        browserInfo.name === "edga"
      ) {
        browserInfo.name = "edge";
        return true;
      }

      if (
        (browserInfo.name === "chrome" && browserInfoList.length === 2) ||
        browserInfo.name === "crios"
      ) {
        browserInfo.name = "chrome";
        return true;
      }

      if (
        browserInfo.name === "safari" &&
        !browserInfoList.some((b) => b.name === "chrome")
      ) {
        const safariVersion = browserInfoList.find((b) => b.name === "version");
        if (safariVersion) browserInfo.version = safariVersion.version;
        return true;
      }

      if (browserInfo.name === "firefox" || browserInfo.name === "fxios") {
        browserInfo.name = "firefox";
        return true;
      }

      return false;
    });

    return (
      res ?? {
        name: "Unknown",
        version: "Unknown Version",
      }
    );
  };

  const parseBrowserList = () => {
    const userAgent = window.navigator.userAgent
      .toLocaleLowerCase()
      .replace(/\s*\(.*?\)\s*/g, " ");
    const browsersInfoString = userAgent
      .split(" ")
      .filter((value) => value.includes("/"))
      .slice(2); // Skip Mozilla/5.0 and Engine info

    return browsersInfoString.map((browserString): BrowserInfo => {
      const elements = browserString.split("/");
      return {
        name: elements[0],
        version: elements[1],
      };
    });
  };

  useEffect(() => {
    const validateBrowserVersion = (browserInfo: BrowserInfo): boolean => {
      if (browserInfo.version.includes("Unknown Version")) return false;
      const browserMajorVersion = parseInt(
        browserInfo.version.split(".")[0],
        10,
      );
      return browserMajorVersion >= MIN_SUPPORTED_VERSIONS[browserInfo.name];
    };

    const validateBrowserInfo = async () => {
      const candidateBrowser = await getBrowserInfo();
      setBrowserInfo({
        name: capitalizeFirstChar(candidateBrowser.name),
        version: extractMajorVer(candidateBrowser.version) ?? "",
      });

      if (
        SUPPORTED_BROWSERS.includes(candidateBrowser.name) &&
        validateBrowserVersion(candidateBrowser)
      ) {
        setStatus("passed");
      } else {
        setStatus("failed");
      }
    };

    if (shouldRun) {
      setTimeout(() => {
        validateBrowserInfo();
      }, 1000);
    }
  }, [shouldRun]);

  return {
    browserInfo,
    browserCheckStatus: status,
  };
};
